import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Sparkles, Trash2, MailOpen, LogOut, RefreshCw, Inbox, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORY_STYLES = {
  technical: "bg-sky-50 text-sky-800 border-sky-200",
  commercial: "bg-blue-50 text-blue-800 border-blue-200",
  management: "bg-amber-50 text-amber-800 border-amber-200",
  general: "bg-slate-100 text-slate-600 border-slate-200",
  direct: "bg-violet-50 text-violet-800 border-violet-200",
};

const formatDate = (iso) => {
  try {
    return new Date(iso).toLocaleString("de-DE", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

export default function Admin() {
  const [user, setUser] = useState(null);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loginError, setLoginError] = useState("");
  const [inquiries, setInquiries] = useState([]);
  const [busyId, setBusyId] = useState("");

  const loadInquiries = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/inquiries`, { withCredentials: true });
      setInquiries(data.inquiries);
    } catch (e) {
      if (e.response?.status === 401) setUser(false);
    }
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/auth/me`, { withCredentials: true });
        setUser(data);
      } catch {
        setUser(false);
      }
    })();
  }, []);

  useEffect(() => {
    if (user) loadInquiries();
  }, [user, loadInquiries]);

  const login = async (e) => {
    e.preventDefault();
    setLoginError("");
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password }, { withCredentials: true });
      setUser(data);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setLoginError(typeof detail === "string" ? detail : "Anmeldung fehlgeschlagen — bitte Zugangsdaten prüfen.");
    }
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } finally {
      setUser(false);
      setInquiries([]);
    }
  };

  const markRead = async (id) => {
    try {
      await axios.post(`${API}/admin/inquiries/${id}/read`, {}, { withCredentials: true });
      setInquiries((prev) => prev.map((i) => (i.id === id ? { ...i, read: true } : i)));
    } catch {
      toast.error("Aktion fehlgeschlagen.");
    }
  };

  const remove = async (id) => {
    try {
      await axios.delete(`${API}/admin/inquiries/${id}`, { withCredentials: true });
      setInquiries((prev) => prev.filter((i) => i.id !== id));
      toast.success("Anfrage gelöscht.");
    } catch {
      toast.error("Löschen fehlgeschlagen.");
    }
  };

  const assist = async (id) => {
    setBusyId(id);
    try {
      const { data } = await axios.post(`${API}/admin/inquiries/${id}/assist`, {}, { withCredentials: true });
      setInquiries((prev) =>
        prev.map((i) => (i.id === id ? { ...i, summary: data.summary, reply_draft: data.reply_draft, read: true } : i))
      );
      toast.success("KI-Auswertung erstellt.");
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "KI momentan nicht verfügbar.");
    } finally {
      setBusyId("");
    }
  };

  if (user === null) {
    return (
      <div data-testid="admin-loading" className="min-h-screen bg-paper flex items-center justify-center">
        <Loader2 className="animate-spin text-[#003940]" size={28} />
      </div>
    );
  }

  if (user === false) {
    return (
      <div className="min-h-screen bg-paper flex items-center justify-center px-4 font-jakarta">
        <form
          onSubmit={login}
          data-testid="admin-login-form"
          className="w-full max-w-md p-8 sm:p-10 rounded-3xl bg-surface border border-slate-200 shadow-2xl space-y-6"
        >
          <div className="text-center">
            <img src="/logo.png" alt="BUNDLER FM" className="h-9 w-auto inline-block" />
            <p className="mt-2 text-xs font-mono uppercase tracking-[0.25em] text-[#003940]">Admin-Bereich</p>
          </div>
          <div>
            <label htmlFor="admin-email" className="block text-xs font-mono uppercase tracking-[0.2em] text-slate-500 mb-2">
              E-Mail
            </label>
            <input
              id="admin-email"
              data-testid="admin-email-input"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-4 py-3 rounded-xl bg-[#F8F7F4] border border-slate-200 text-[#0A272C] focus:outline-none focus:border-[#003940] focus:ring-1 focus:ring-[#003940] transition-all text-sm"
            />
          </div>
          <div>
            <label htmlFor="admin-password" className="block text-xs font-mono uppercase tracking-[0.2em] text-slate-500 mb-2">
              Passwort
            </label>
            <input
              id="admin-password"
              data-testid="admin-password-input"
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-4 py-3 rounded-xl bg-[#F8F7F4] border border-slate-200 text-[#0A272C] focus:outline-none focus:border-[#003940] focus:ring-1 focus:ring-[#003940] transition-all text-sm"
            />
          </div>
          {loginError && (
            <p data-testid="admin-login-error" className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-xl px-4 py-3">
              {loginError}
            </p>
          )}
          <button
            type="submit"
            data-testid="admin-login-submit"
            className="w-full px-7 py-3.5 rounded-full bg-[#003940] hover:bg-[#002A2F] text-white font-semibold text-sm transition-all duration-200"
          >
            Anmelden
          </button>
          <a href="/" data-testid="admin-back-link" className="block text-center text-xs text-slate-500 hover:text-[#003940] transition-colors font-mono">
            Zurück zur Website
          </a>
        </form>
      </div>
    );
  }

  const unread = inquiries.filter((i) => !i.read).length;

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-paper font-jakarta text-[#0A272C]">
      <header className="border-b border-slate-200 bg-surface/80 backdrop-blur-xl sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-4 sm:px-8 py-4 flex items-center justify-between">
          <div>
            <img src="/logo.png" alt="BUNDLER FM" className="h-7 w-auto" />
            <p className="text-xs font-mono uppercase tracking-[0.2em] text-slate-500">Admin · Anfragen</p>
          </div>
          <div className="flex items-center gap-3">
            <span data-testid="admin-unread-badge" className="text-xs font-mono text-[#003940] border border-[#003940]/30 rounded-full px-3 py-1.5 bg-[#003940]/5">
              {unread} neu
            </span>
            <button
              onClick={loadInquiries}
              data-testid="admin-refresh-button"
              className="p-2.5 rounded-full border border-slate-200 text-slate-500 hover:text-[#003940] hover:border-[#003940]/50 transition-all"
              aria-label="Aktualisieren"
            >
              <RefreshCw size={16} />
            </button>
            <button
              onClick={logout}
              data-testid="admin-logout-button"
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full border border-slate-200 text-slate-600 hover:text-red-600 hover:border-red-300 transition-all text-sm"
            >
              <LogOut size={15} />
              Abmelden
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-8 py-10 space-y-6">
        {inquiries.length === 0 && (
          <div data-testid="admin-empty-state" className="text-center py-24 text-slate-500">
            <Inbox size={40} className="mx-auto mb-4 text-slate-300" />
            <p className="text-sm">Keine Anfragen vorhanden.</p>
          </div>
        )}

        {inquiries.map((inq) => (
          <article
            key={inq.id}
            data-testid={`inquiry-card-${inq.id}`}
            className={`p-6 sm:p-8 rounded-2xl bg-surface border transition-all ${
              inq.read ? "border-slate-200" : "border-[#003940]/50 shadow-[0_0_25px_rgba(0,75,135,0.08)]"
            }`}
          >
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div>
                <div className="flex flex-wrap items-center gap-3">
                  <h3 className="text-lg font-semibold text-[#0A272C]">{inq.name}</h3>
                  {!inq.read && (
                    <span className="text-[10px] font-mono uppercase tracking-[0.15em] text-white bg-[#003940] rounded-full px-2.5 py-0.5">
                      Neu
                    </span>
                  )}
                  <span
                    data-testid={`inquiry-category-${inq.id}`}
                    className={`text-[10px] font-mono uppercase tracking-[0.15em] border rounded-full px-2.5 py-1 ${
                      CATEGORY_STYLES[inq.category] || CATEGORY_STYLES.general
                    }`}
                  >
                    {inq.category_label}
                  </span>
                </div>
                <p className="mt-1.5 text-xs font-mono text-slate-500">
                  {inq.email} · {formatDate(inq.created_at)} · an {inq.routed_label}
                </p>
              </div>
              <div className="flex items-center gap-2">
                {!inq.read && (
                  <button
                    onClick={() => markRead(inq.id)}
                    data-testid={`inquiry-read-${inq.id}`}
                    className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full border border-slate-200 text-xs text-slate-600 hover:text-[#003940] hover:border-[#003940]/50 transition-all"
                  >
                    <MailOpen size={13} />
                    Gelesen
                  </button>
                )}
                <button
                  onClick={() => assist(inq.id)}
                  disabled={busyId === inq.id}
                  data-testid={`inquiry-assist-${inq.id}`}
                  className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full bg-[#003940] hover:bg-[#002A2F] disabled:opacity-60 text-white text-xs font-semibold transition-all"
                >
                  {busyId === inq.id ? <Loader2 size={13} className="animate-spin" /> : <Sparkles size={13} />}
                  KI-Auswertung
                </button>
                <button
                  onClick={() => remove(inq.id)}
                  data-testid={`inquiry-delete-${inq.id}`}
                  className="p-2 rounded-full border border-slate-200 text-slate-400 hover:text-red-600 hover:border-red-300 transition-all"
                  aria-label="Löschen"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            </div>

            <p className="mt-4 text-sm text-slate-700 leading-relaxed whitespace-pre-wrap">{inq.message}</p>

            {inq.summary && (
              <div data-testid={`inquiry-summary-${inq.id}`} className="mt-5 p-5 rounded-xl bg-[#EAF4F5] border border-[#003940]/20">
                <p className="text-[10px] font-mono uppercase tracking-[0.2em] text-[#003940]">KI-Zusammenfassung</p>
                <p className="mt-2 text-sm text-slate-700 leading-relaxed whitespace-pre-wrap">{inq.summary}</p>
              </div>
            )}
            {inq.reply_draft && (
              <div data-testid={`inquiry-draft-${inq.id}`} className="mt-4 p-5 rounded-xl bg-[#F8F7F4] border border-slate-200">
                <p className="text-[10px] font-mono uppercase tracking-[0.2em] text-slate-500">Antwortentwurf (DE)</p>
                <p className="mt-2 text-sm text-slate-700 leading-relaxed whitespace-pre-wrap">{inq.reply_draft}</p>
              </div>
            )}
          </article>
        ))}
      </main>
    </div>
  );
}
